import React, { useState, useEffect, useRef } from "react";
import {
    Button,
    TextField,
    Paper,
    Box,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputLabel,
    FormHelperText,
    LinearProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import HttpService from '../services/httpService';
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import { connect } from "react-redux";
import BrandedFormBackground from "./brandedFormBackground";

const useStyles = makeStyles((theme) => ({
    fileInput: {
        display: 'none',
    },
    uploadButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: '10px 20px',
        backgroundColor: '#4caf50',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#45a049',
        },
        '&.Mui-disabled': {
            backgroundColor: '#ccc',
            color: '#666',
        }
    },
    uploadProgress: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    previewImage: {
        maxWidth: "100%",
        maxHeight: "200px",
        marginTop: theme.spacing(1),
        borderRadius: "4px",
        border: "1px solid #ddd"
    },
    uploadContainer: {
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: theme.spacing(2),
        textAlign: 'center',
        backgroundColor: '#f9f9f9',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: '#f0f0f0',
        borderRadius: '4px'
    },
    removeFileButton: {
        marginLeft: theme.spacing(1),
        minWidth: 'auto',
        padding: '4px 8px',
        backgroundColor: '#f44336',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#d32f2f',
        }
    }
}));

// Standalone Physical Site Visit bank-details collection page, at
// /details/site-visit-bank-details-collect — deliberately not shared with the
// referral bank-details page (bankDetailsCollect.js). Shows read-only prefilled
// referrer/amount context, requires a cancelled-cheque/bank-receipt upload, and
// confirms before submitting.
const SiteVisitBankDetailsCollect = (props) => {
    const classes = useStyles();
    const chequeInputRef = useRef(null);

    const [formData, setFormData] = useState({
        email: "",
        accountHolder: "",
        bankName: "",
        branchName: "",
        accountNumber: "",
        confirmAccountNumber: "",
        ifscCode: "",
        uniqueLeadNumber: "",
        customerId: "",
        leadId: "",
        chequeDocumentLink: ""
    });

    const [errors, setErrors] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [siteVisitDetails, setSiteVisitDetails] = useState(null);
    const [chequeFile, setChequeFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFileLink, setUploadedFileLink] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);

    // The link only ever carries an opaque, signed token — not raw customerId/
    // leadId/uniqueLeadNumber — so those are resolved server-side and read back
    // from the details response below, not from the URL.
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const token = queryParams.get('token');

        if (token) {
            fetchSiteVisitDetails(token);
        } else {
            setCheckingStatus(false);
            props.setSnackInfo("This link is invalid or incomplete", "error");
        }
    }, []);

    // Check upfront whether this referrer has already submitted bank details for
    // this lead, so a returning submitter sees a confirmation state, not the form again.
    const checkAlreadySubmitted = async (customerId, leadId) => {
        try {
            const response = await HttpService.getBankDetailsStatus({ customerId, leadId, type: "siteVisit" });
            if (response.data?.submitted) {
                setAlreadySubmitted(true);
            }
        } catch (error) {
            console.error("Error checking bank details status:", error);
        } finally {
            setCheckingStatus(false);
        }
    };

    // Function to fetch prefill details for the Physical Site Visit flow — resolves
    // the token into the underlying customerId/leadId/uniqueLeadNumber too.
    const fetchSiteVisitDetails = async (token) => {
        try {
            props.setLoading(true);
            const response = await HttpService.getSiteVisitPaymentDetails(token);
            props.setLoading(false);

            if (response.data && response.data.data) {
                const data = response.data.data;
                setSiteVisitDetails(data);
                setFormData(prevData => ({
                    ...prevData,
                    email: data.email || "",
                    customerId: data.customerId || "",
                    leadId: data.leadId || "",
                    uniqueLeadNumber: data.uniqueLeadNumber || "",
                }));
                props.setSnackInfo("Details loaded successfully", "success");
                if (data.customerId && data.leadId) {
                    checkAlreadySubmitted(data.customerId, data.leadId);
                } else {
                    setCheckingStatus(false);
                }
            } else {
                props.setSnackInfo("Failed to load details", "error");
                setCheckingStatus(false);
            }
        } catch (error) {
            props.setLoading(false);
            setCheckingStatus(false);
            console.error("Error fetching site visit details:", error);
            props.setSnackInfo(error?.response?.data?.message || "Error loading details", "error");
        }
    };

    const onChequeUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const extension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ["pdf", "jpg", "jpeg", "png"];

        if (!allowedExtensions.includes(extension)) {
            setChequeFile(null);
            setUploadedFileLink("");
            setPreviewUrl("");
            setFileError("Invalid file type. Please upload a PDF, JPG, JPEG, or PNG file.");
            props.setSnackInfo("Invalid file type. Please upload a pdf, jpg, jpeg, or png file.", "warning");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setFileError("File size should be less than 5MB");
            props.setSnackInfo("File size should be less than 5MB", "warning");
            return;
        }

        const fd = new FormData();
        const fileName = `chequeOrReceipt_${formData.customerId || 'customer'}_${Date.now()}.${extension}`;
        fd.append('file', file, fileName);

        try {
            setIsUploading(true);
            props.setLoading(true);
            setFileError("");

            const fileUploadRes = await HttpService.uploadFile(fd);

            if (fileUploadRes.data) {
                const fileUrl = fileUploadRes.data;
                setUploadedFileLink(fileUrl);
                setPreviewUrl(fileUrl);
                setChequeFile(file);
                setFormData(prev => ({
                    ...prev,
                    chequeDocumentLink: fileUrl
                }));
                props.setSnackInfo("Document uploaded successfully", "success");
            }
        } catch (err) {
            console.error("Error uploading cheque/receipt:", err);
            props.setSnackInfo(err?.response?.data ?? "Failed to upload document", "error");
            setFileError("Failed to upload document");
            setUploadedFileLink("");
            setChequeFile(null);
        } finally {
            setIsUploading(false);
            props.setLoading(false);
        }
    };

    const validateForm = () => {
        let tempErrors = {};
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        const nameRegex = /^[A-Za-z\s]+$/;

        if (!emailRegex.test(formData.email)) {
            tempErrors.email = "Enter a valid email address";
        }

        if (!nameRegex.test(formData.accountHolder)) {
            tempErrors.accountHolder = "Name should contain only letters and spaces";
        }

        if (!nameRegex.test(formData.bankName)) {
            tempErrors.bankName = "Bank name should contain only letters and spaces";
        }
        if (!nameRegex.test(formData.branchName)) {
            tempErrors.branchName = "Branch name should contain only letters and spaces";
        }

        if (formData.accountNumber !== formData.confirmAccountNumber) {
            tempErrors.confirmAccountNumber = "Account numbers do not match";
        }

        if (!formData.chequeDocumentLink) {
            tempErrors.chequeDocument = "Please upload a cancelled cheque or bank receipt";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const doSubmit = async () => {
        try {
            props.setLoading(true);
            const dataToSubmit = {
                ...formData,
                customerId: formData.customerId,
                uniqueLeadNumber: formData.uniqueLeadNumber,
                leadId: formData.leadId,
            };

            const response = await HttpService.siteVisitBankDetailsSave(dataToSubmit);
            props.setLoading(false);

            if (response.data.success) {
                const wasAlreadySubmitted = /already submitted/i.test(response.data.message || "");
                setDialogMessage(
                    wasAlreadySubmitted
                        ? "You've already submitted your bank details for this request. No further action is needed — we'll get back to you once the process is complete."
                        : "Thank you for submitting your bank details. We will get back to you once the process is complete."
                );
                setOpenDialog(true);
                setAlreadySubmitted(true);
                props.setSnackInfo(wasAlreadySubmitted ? "Bank details were already submitted" : "Bank details submitted successfully", wasAlreadySubmitted ? "info" : "success");
            } else {
                props.setSnackInfo(response.data.message || "Failed to submit bank details", "error");
            }
        } catch (error) {
            props.setLoading(false);
            console.error("Error submitting bank details:", error);

            if (error?.response && error?.response?.status === 403) {
                props.setSnackInfo("Enter Account and IFSC details correctly. Starting of IFSC should be in CAPITAL and Account number should be 9 digit", "error");
            } else if (error?.response && error?.response?.status === 400) {
                props.setSnackInfo(error?.response?.data?.message || "Bank details already submitted", "error");
            } else {
                props.setSnackInfo("Failed to connect to server. Please try again later.", "error");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        // Double-check before submitting, per requirement for this flow.
        setShowConfirm(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirm(false);
        await doSubmit();
    };

    const handleReset = () => {
        const { customerId, uniqueLeadNumber, leadId } = formData;
        setFormData({
            email: siteVisitDetails?.email || "",
            accountHolder: "",
            bankName: "",
            branchName: '',
            accountNumber: "",
            confirmAccountNumber: "",
            ifscCode: "",
            uniqueLeadNumber: uniqueLeadNumber || "",
            customerId,
            leadId: leadId || "",
            chequeDocumentLink: ""
        });
        setChequeFile(null);
        setFileError("");
        setUploadedFileLink("");
        setPreviewUrl("");
        if (chequeInputRef.current) {
            chequeInputRef.current.value = '';
        }
        setErrors({});
    };

    if (checkingStatus) {
        return (
            <BrandedFormBackground>
                <Paper style={{ padding: "40px", maxWidth: "500px", margin: "auto", marginTop: "20px", textAlign: "center", backgroundColor: "rgba(255,255,255,0.92)" }}>
                    <Typography variant="body1" color="textSecondary">Loading...</Typography>
                </Paper>
            </BrandedFormBackground>
        );
    }

    if (alreadySubmitted) {
        return (
            <BrandedFormBackground>
                <Paper style={{ padding: "40px", maxWidth: "500px", margin: "auto", marginTop: "20px", textAlign: "center", backgroundColor: "rgba(255,255,255,0.92)" }}>
                    <Typography variant="h5" style={{ marginBottom: "10px" }}>You've Already Submitted</Typography>
                    <Typography variant="body1" color="textSecondary">
                        Your bank details have already been submitted for this request. We will get back to you once the process is complete.
                    </Typography>
                </Paper>
            </BrandedFormBackground>
        );
    }

    return (
        <BrandedFormBackground>
        <Paper style={{ padding: "20px", maxWidth: "500px", margin: "auto", marginTop: "20px", marginBottom: "20px", backgroundColor: "rgba(255,255,255,0.92)" }}>
            <Typography variant="h5" style={{ marginBottom: "10px" }}>
                Physical Site Visit — Bank Details Collection
            </Typography>
            <form onSubmit={handleSubmit}>
                <TextField
                    fullWidth
                    label="Customer Name"
                    value={siteVisitDetails?.clientName || ""}
                    InputProps={{ readOnly: true }}
                    margin="dense"
                    style={{ backgroundColor: "#f0f0f0" }}
                />
                <TextField
                    fullWidth
                    label="Mobile No."
                    value={siteVisitDetails?.mobile || ""}
                    InputProps={{ readOnly: true }}
                    margin="dense"
                    style={{ backgroundColor: "#f0f0f0" }}
                />
                <TextField
                    fullWidth
                    label="Apartment Name & Address"
                    value={siteVisitDetails?.apartmentAddress || ""}
                    InputProps={{ readOnly: true }}
                    margin="dense"
                    style={{ backgroundColor: "#f0f0f0" }}
                />
                <TextField
                    fullWidth
                    label="Amount"
                    value={siteVisitDetails?.amount ? `₹${siteVisitDetails.amount}` : ""}
                    InputProps={{ readOnly: true }}
                    margin="dense"
                    style={{ backgroundColor: "#f0f0f0" }}
                />
                <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    InputProps={{ readOnly: true }}
                    required
                    margin="dense"
                    error={!!errors.email}
                    helperText={errors.email}
                />
                <TextField
                    fullWidth
                    label="Name of the Account Holder"
                    name="accountHolder"
                    value={formData.accountHolder}
                    onChange={handleChange}
                    required
                    margin="dense"
                    error={!!errors.accountHolder}
                    helperText={errors.accountHolder}
                />
                <TextField
                    fullWidth
                    label="Name of the Bank"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    required
                    margin="dense"
                    error={!!errors.bankName}
                    helperText={errors.bankName}
                />
                <TextField
                    fullWidth
                    label="Name of Branch"
                    name="branchName"
                    value={formData.branchName}
                    onChange={handleChange}
                    required
                    margin="dense"
                    error={!!errors.branchName}
                    helperText={errors.branchName}
                />
                <TextField
                    fullWidth
                    label="Account Number"
                    name="accountNumber"
                    type="password"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    required
                    margin="dense"
                />
                <TextField
                    fullWidth
                    label="Confirm Account Number"
                    name="confirmAccountNumber"
                    type="number"
                    value={formData.confirmAccountNumber}
                    onChange={handleChange}
                    required
                    margin="dense"
                    error={!!errors.confirmAccountNumber}
                    helperText={errors.confirmAccountNumber}
                />
                <TextField
                    fullWidth
                    label="IFSC Code"
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={handleChange}
                    required
                    margin="dense"
                />
                <TextField
                    fullWidth
                    label="Your unique Lead Number or Project No"
                    name="uniqueLeadNumber"
                    value={formData.uniqueLeadNumber}
                    InputProps={{ readOnly: true }}
                    required
                    margin="dense"
                />

                <Box mt={2} mb={2}>
                    <InputLabel style={{ marginBottom: "8px", fontWeight: 500 }}>
                        Upload Cancelled Cheque / Bank Receipt *
                    </InputLabel>

                    <div className={classes.uploadContainer}>
                        <input
                            ref={chequeInputRef}
                            id="cheque-upload-input"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={onChequeUpload}
                            className={classes.fileInput}
                            disabled={isUploading}
                        />

                        <label htmlFor="cheque-upload-input">
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<CloudUploadIcon />}
                                className={classes.uploadButton}
                                disabled={isUploading}
                                style={{
                                    backgroundColor: "#F47E46",
                                    color: "#FFF2EB",
                                    padding: "10px 24px",
                                    fontSize: "14px",
                                    textTransform: "none",
                                    marginBottom: "10px"
                                }}
                            >
                                {isUploading ? "Uploading..." : "Upload Cancelled Cheque / Receipt"}
                            </Button>
                        </label>

                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: "5px" }}>
                            Click the button above to select a file
                        </Typography>

                        {chequeFile && !uploadedFileLink && (
                            <div className={classes.fileInfo}>
                                <Typography variant="body2">
                                    📄 {chequeFile.name} ({(chequeFile.size / 1024).toFixed(2)} KB)
                                </Typography>
                                <Button
                                    size="small"
                                    className={classes.removeFileButton}
                                    onClick={() => {
                                        setChequeFile(null);
                                        setFileError("");
                                        if (chequeInputRef.current) {
                                            chequeInputRef.current.value = '';
                                        }
                                    }}
                                    disabled={isUploading}
                                >
                                    Remove
                                </Button>
                            </div>
                        )}

                        {isUploading && (
                            <Box className={classes.uploadProgress}>
                                <LinearProgress />
                                <Typography variant="caption">Uploading document... Please wait</Typography>
                            </Box>
                        )}

                        {uploadedFileLink && (
                            <Box mt={1}>
                                <div className={classes.fileInfo} style={{ backgroundColor: '#e8f5e9' }}>
                                    <Typography variant="body2" color="primary">
                                        ✓ Document uploaded successfully: {chequeFile?.name}
                                    </Typography>
                                    <Button
                                        size="small"
                                        className={classes.removeFileButton}
                                        onClick={() => {
                                            setUploadedFileLink("");
                                            setPreviewUrl("");
                                            setChequeFile(null);
                                            setFormData(prev => ({ ...prev, chequeDocumentLink: "" }));
                                            if (chequeInputRef.current) {
                                                chequeInputRef.current.value = '';
                                            }
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </div>

                                {previewUrl && (previewUrl.match(/\.(jpg|jpeg|png)$/i)) && (
                                    <Box mt={1}>
                                        <img
                                            src={previewUrl}
                                            alt="Cheque/receipt preview"
                                            className={classes.previewImage}
                                            style={{ maxWidth: '100%', maxHeight: '200px' }}
                                        />
                                        <br />
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={() => window.open(previewUrl, '_blank')}
                                            style={{ marginTop: 8 }}
                                        >
                                            View Full Size
                                        </Button>
                                    </Box>
                                )}

                                {previewUrl && (previewUrl.match(/\.(pdf)$/i)) && (
                                    <Box mt={1}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => window.open(previewUrl, '_blank')}
                                        >
                                            View Uploaded PDF
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {(!!errors.chequeDocument || fileError) && (
                            <FormHelperText error style={{ marginTop: "5px" }}>
                                {errors.chequeDocument || fileError}
                            </FormHelperText>
                        )}

                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: "10px" }}>
                            Accepted formats: PDF, JPG, JPEG, PNG (Max size: 5MB)
                        </Typography>
                    </div>
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between">
                    <Button type="submit" variant="contained" color="primary" disabled={isUploading}>
                        Submit
                    </Button>
                    <Button type="button" variant="contained" color="secondary" onClick={handleReset} disabled={isUploading}>
                        Reset
                    </Button>
                </Box>
            </form>
            {/* Success Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Submission Successful</DialogTitle>
                <DialogContent>
                    <DialogContentText>{dialogMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Confirm-before-submit Dialog */}
            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please double check your bank details before submitting. Once submitted, do you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirm(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmSubmit} color="primary" variant="contained" autoFocus>
                        Yes, Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
        </BrandedFormBackground>
    );
};

export default connect(null, { setLoading, setSnackInfo })(SiteVisitBankDetailsCollect);
